<template>
  <!--
    세션 만료 시 화면 안에서 재로그인하는 인-페이지 모달.
    api.ts interceptor 가 authStore.requestSessionRecovery() 로 sessionRecovering=true 를
    세팅하면 이 모달이 뜬다. 로그인 성공 → resolveSessionRecovery(token) 로 interceptor 를
    깨워 실패했던 원래 요청을 재시도(백단 페이지는 그대로 유지)하고, 로그아웃 → cancelSessionRecovery().
    별도 브라우저 창(window.open)을 쓰지 않으므로 팝업 차단 이슈가 없다.
  -->
  <el-dialog
    :model-value="authStore.sessionRecovering"
    :title="t('SESSION_EXPIRED_TITLE')"
    width="420px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    align-center
    append-to-body
    class="session-expired-dialog"
  >
    <p class="session-desc">{{ t('SESSION_EXPIRED_DESC') }}</p>

    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      size="large"
      @submit.prevent="handleLogin"
    >
      <el-form-item prop="username">
        <el-input
          v-model="form.username"
          :placeholder="t('LBL_USERNAME')"
          :prefix-icon="User"
          autocomplete="username"
        />
      </el-form-item>
      <el-form-item prop="password">
        <el-input
          v-model="form.password"
          type="password"
          :placeholder="t('LBL_PASSWORD')"
          :prefix-icon="Lock"
          show-password
          autocomplete="current-password"
          @keyup.enter="handleLogin"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="handleLogout">{{ t('HDR_LOGOUT') }}</el-button>
      <el-button type="primary" :loading="loading" @click="handleLogin">
        {{ t('LOGIN_BTN') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { User, Lock } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'
import { ElMessageBox } from 'element-plus'
import { useAuthStore } from '~/stores/auth'

const { t } = useLocale()
const authStore = useAuthStore()

const loading = ref(false)
const formRef = ref<FormInstance>()
const form = reactive({ username: '', password: '' })

const rules = computed<FormRules>(() => ({
  username: [{ required: true, message: t('LOGIN_REQUIRED_ID'), trigger: 'blur' }],
  password: [{ required: true, message: t('LOGIN_REQUIRED_PW'), trigger: 'blur' }],
}))

// 모달이 다시 열릴 때마다 비밀번호 입력값/검증 상태를 초기화한다.
watch(
  () => authStore.sessionRecovering,
  (open) => {
    if (open) {
      form.username = authStore.user?.username ?? form.username
      form.password = ''
      formRef.value?.clearValidate()
    }
  }
)

const handleLogin = async () => {
  if (!formRef.value) return
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    loading.value = true
    try {
      await authStore.login(form)
      // 새 accessToken 으로 interceptor 를 깨워 원래 요청을 재시도하게 한다.
      authStore.resolveSessionRecovery(authStore.accessToken as string)
      form.password = ''
    } catch (err: any) {
      if (err?.response?.data?.code === 'U005') {
        ElMessageBox.alert(err.response.data.message, '로그인 불가', {
          type: 'warning',
          confirmButtonText: '확인',
        })
      }
      // 그 외(잘못된 자격증명 등)는 login.vue 와 동일하게 모달을 열어 둔 채
      // 사용자가 다시 입력하도록 둔다(/auth/login 은 api.ts 전역 에러 알림 대상에서 제외됨).
    } finally {
      loading.value = false
    }
  })
}

const handleLogout = () => {
  authStore.cancelSessionRecovery()
}
</script>

<style scoped>
.session-desc {
  margin: 0 0 20px;
  font-size: 14px;
  color: #606266;
}
</style>
